import './style.scss';

import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button } from '@components/Button/Button';
import { Card } from '@components/common/Card/Card';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  useDeleteMedicineDictionaryRecordMutation,
  useGetDictionariesQuery,
  useGetMedicineRecordQuery,
  usePersistMedicineDictionaryMutation,
} from '@/api/dictionaries/api';
import { medicineCardConfig } from '@/pages/Pharmacy/components/card/card.config';
import { createTablePartModalConfig } from '@/pages/Pharmacy/components/card/modals.config';
import { columns } from '@/pages/Pharmacy/components/card/table.config';
import { createModalConfig } from '@/pages/Pharmacy/components/modal.config';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { hasPermission } from '@/shared/security/hasPermission';
import { pharmacy } from '@/shared/security/permissions/pharmacy';
import { withSecurity } from '@/shared/security/withSecurity';
import { mapTableIndexWithPage } from '@/utils/utils';

export const PharmacyDictionariesCard = withSecurity(({ auth }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [isTablePartModalOpen, setIsTablePartModalOpen] = useState(false);
  const [isTablePartEditModalOpen, setIsTablePartEditModalOpen] = useState(false);
  const [deleteTablePartModal, setDeleteTablePartModal] = useState(false);
  const [deleteRecordModal, setDeleteRecordModal] = useState(false);
  const [editRecordModal, setEditRecordModal] = useState(false);

  const [editedPart, setEditedPart] = useState<null | any>(null);

  const { data: dictionaries, isLoading: isDictionariesLoading } =
    useGetDictionariesQuery();

  const { data: record, isLoading: isRecordLoading } = useGetMedicineRecordQuery({ id });
  const [saveRecord, { isLoading: isRecordSaving, isSuccess: isSaveSuccess }] =
    usePersistMedicineDictionaryMutation();

  const [deleteRecord, { isSuccess: isDeleteSuccess }] =
    useDeleteMedicineDictionaryRecordMutation();

  const onDrugEdit = async (payload: any) => {
    await saveRecord({ ...record, ...payload });
  };

  const onTablePartsAdd = async (payload: any) => {
    await saveRecord({ ...record, tableParts: [...(record?.tableParts ?? []), payload] });
  };

  const onTablePartsEdit = async (patch: any) => {
    await saveRecord({
      ...record,
      tableParts: record?.tableParts?.map((part: any) =>
        part.id === editedPart?.id ? { ...part, ...patch } : part,
      ),
    });
  };

  const onTablePartDelete = async (tablePartId: any) => {
    await saveRecord({
      ...record,
      tableParts: record?.tableParts.filter(
        ({ id }: { id: string }) => id !== tablePartId,
      ),
    });
  };

  useEffect(() => {
    if (isSaveSuccess) {
      setIsTablePartModalOpen(false);
      setIsTablePartEditModalOpen(false);
      setEditRecordModal(false);
      setDeleteTablePartModal(false);
      setEditedPart(null);
    }
  }, [isSaveSuccess]);

  useEffect(() => {
    if (isDeleteSuccess) {
      setDeleteRecordModal(false);
      navigate(-1);
    }
  }, [isDeleteSuccess]);

  return (
    <>
      <Modal
        centered
        mode={'form'}
        schema={createTablePartModalConfig(dictionaries)}
        title={'Добавить показания к применению'}
        open={isTablePartModalOpen}
        onSubmit={onTablePartsAdd}
        onCancel={() => {
          setIsTablePartModalOpen(false);
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        formKey={'createTablePartForm'}
        confirmLoading={isRecordSaving}
      />
      <Modal
        centered
        mode={'form'}
        data={editedPart}
        schema={createTablePartModalConfig(dictionaries)}
        title={'Редактировать показания к применению'}
        open={isTablePartEditModalOpen}
        onSubmit={onTablePartsEdit}
        onCancel={() => {
          setIsTablePartEditModalOpen(false);
          setEditedPart(null);
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        formKey={editedPart?.id}
        confirmLoading={isRecordSaving}
      />
      <Modal
        mode={'form'}
        schema={createModalConfig(dictionaries?.DRUG_TYPE ?? [])}
        data={record}
        title={'Редактировать препарат'}
        open={editRecordModal}
        onSubmit={onDrugEdit}
        onCancel={() => {
          setEditRecordModal(false);
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        formKey={'editDrugForm'}
        confirmLoading={isRecordSaving}
      />
      <Modal
        mode={'confirm'}
        open={deleteTablePartModal}
        onCancel={() => {
          setEditedPart(null);
          setDeleteTablePartModal(false);
        }}
        onSubmit={async () => {
          await onTablePartDelete(editedPart?.id);
        }}
        text={
          'Вы уверены, что хотите удалить выбранные данные? При выборе «Удалить» данные восстановлению не подлежат'
        }
        submitText={'Да'}
        cancelText={'Нет'}
      />
      <Modal
        mode={'confirm'}
        open={deleteRecordModal}
        onCancel={() => {
          setDeleteRecordModal(false);
        }}
        onSubmit={async () => {
          await deleteRecord({ id: record?.id });
        }}
        text={
          'Вы уверены, что хотите удалить выбранные данные? При выборе «Удалить» данные восстановлению не подлежат'
        }
        submitText={'Да'}
        cancelText={'Нет'}
      />
      <PageTitle title={'Сведения о препарате'} />
      <Card
        viewMode
        schema={medicineCardConfig(dictionaries?.DRUG_TYPE)}
        data={record}
        loading={isRecordLoading || isDictionariesLoading || isRecordSaving}
        actions={{
          onBack: () => {
            navigate(-1);
          },
          onEdit: {
            action: () => setEditRecordModal(true),
            security: {
              permissions: pharmacy.dictionary.editDrug,
              level: OrganizationLevelType.DISTRICT,
            },
          },
          extra: [
            {
              text: 'Удалить',
              action: () => {
                setDeleteRecordModal(true);
              },
              buttonProps: {
                type: 'primary',
              },
              security: {
                permissions: pharmacy.dictionary.deleteDrug,
                level: OrganizationLevelType.DISTRICT,
              },
            },
          ],
        }}
      />
      <PageTitle level={4} title={'Назначение'} subTitle={true} />
      <UTable
        size={'small'}
        columns={columns(dictionaries)}
        data={mapTableIndexWithPage(record?.tableParts)}
        loading={isRecordLoading || isDictionariesLoading || isRecordSaving}
        actions={() => [
          {
            icon: <EditOutlined />,
            action: (record) => {
              setEditedPart(record);
              setIsTablePartEditModalOpen(true);
            },
            security: {
              permissions: pharmacy.dictionary.editDrugUseInformation,
              level: OrganizationLevelType.DISTRICT,
            },
          },
          {
            icon: <DeleteOutlined />,
            action: (record) => {
              setEditedPart(record);
              setDeleteTablePartModal(true);
            },
            security: {
              permissions: pharmacy.dictionary.deleteDrugUseInformation,
              level: OrganizationLevelType.DISTRICT,
            },
          },
        ]}
        className={'table-part'}
      />
      <div className={'bottom-buttons'}>
        <Button
          type={'primary'}
          text={'Добавить'}
          onClick={() => {
            setIsTablePartModalOpen(true);
          }}
          display={hasPermission(auth, {
            permissions: pharmacy.dictionary.addDrugUseInformation,
            level: OrganizationLevelType.DISTRICT,
          })}
        />
      </div>
    </>
  );
});
